import React from 'react';
import { ApolloError } from '@apollo/client';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LoadedContent,
} from '@occc-app/design-system';
import { DisplayCategoryFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { DisplayCategoriesTableRow } from './DisplayCategoriesTableRow';

import { routeCreator } from '../utils';

type DisplayCategoriesTableProps = {
  displayCategories?: DisplayCategoryFragment[] | null;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
};

export const DisplayCategoriesTable = ({
  displayCategories,
  loading,
  error,
  paginationProps,
}: DisplayCategoriesTableProps) => {
  const navigate = useNavigate();

  const isUserPermittedToEditDisplayCategory = useIsUserPermittedTo(Feature.DISPLAY_CATEGORY_EDIT);
  const isUserPermittedToDeleteDisplayCategory = useIsUserPermittedTo(
    Feature.DISPLAY_CATEGORY_DELETE,
  );

  const areActionsVisible =
    isUserPermittedToEditDisplayCategory || isUserPermittedToDeleteDisplayCategory;

  const renderData = (items: DisplayCategoryFragment[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rank</TableCell>
              <TableCell>Restriction</TableCell>
              {areActionsVisible && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <DisplayCategoriesTableRow
                key={item.id}
                displayCategory={item}
                onRowClick={(displayCategory) => navigate(routeCreator.details(displayCategory.id))}
                isUserPermittedToEditDisplayCategory={isUserPermittedToEditDisplayCategory}
                isUserPermittedToDeleteDisplayCategory={isUserPermittedToDeleteDisplayCategory}
              />
            ))}
          </TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<DisplayCategoryFragment>
      error={error}
      loading={loading}
      items={displayCategories}
      renderData={renderData}
    />
  );
};
